<template>
  <div class="community-container">
    <div class="community-header">
      <h2>交流社区 <span class="title-tibetan">གལེང་མོལ་ཚོགས་པ།</span></h2>
      <el-button type="primary" @click="showPostDialog = true">
        <el-icon><EditPen /></el-icon>
        发表新帖 <span class="btn-tibetan">གསར་འགོད།</span>
      </el-button>
    </div>
    
    <div class="posts-list">
      <div 
        v-for="post in posts" 
        :key="post.id"
        class="post-card"
      >
        <div class="post-header">
          <div class="user-info">
            <el-avatar :src="post.user.avatar" :size="50">{{ post.user.username[0] }}</el-avatar>
            <div class="user-details">
              <h4>{{ post.user.username }}</h4>
              <span class="post-time">{{ formatDate(post.createdAt) }}</span>
            </div>
          </div>
        </div>
        
        <div class="post-content">
          <p>{{ post.content }}</p>
          <div class="post-images" v-if="post.images && post.images.length > 0">
            <el-image 
              v-for="(img, index) in post.images" 
              :key="index"
              :src="img"
              :preview-src-list="post.images"
              fit="cover"
              class="post-image"
            />
          </div>
        </div>
        
        <div class="post-actions">
          <el-button text @click="toggleComments(post.id)">
            <el-icon><ChatDotRound /></el-icon>
            评论 ({{ post.comments?.length || 0 }})
          </el-button>
          <el-button text @click="likePost(post)" :class="{ 'is-liked': post.likedByMe }">
            <el-icon><StarFilled v-if="post.likedByMe" /><Star v-else /></el-icon>
            {{ post.likedByMe ? '已赞' : '点赞' }} ({{ post.likes || 0 }})
          </el-button>
        </div>
        
        <div class="comments-section" v-if="expandedPosts.includes(post.id)">
          <div class="comment-input">
            <el-input
              v-model="commentInputs[post.id]"
              placeholder="写下你的评论..."
              type="textarea"
              :rows="2"
            />
            <el-button type="primary" @click="submitComment(post.id)">发表评论</el-button>
          </div>
          
          <div class="comments-list">
            <div 
              v-for="comment in post.comments" 
              :key="comment.id"
              class="comment-item"
            >
              <el-avatar :src="comment.user.avatar" :size="35">{{ comment.user.username[0] }}</el-avatar>
              <div class="comment-content">
                <div class="comment-header">
                  <span class="username">{{ comment.user.username }}</span>
                  <span class="comment-time">{{ formatDate(comment.createdAt) }}</span>
                </div>
                <p>{{ comment.content }}</p>
                <el-button text size="small" @click="replyToComment(post.id, comment.id)">
                  回复
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="pagination">
        <el-pagination
          v-model:current-page="currentPage"
          :page-size="pageSize"
          :total="total"
          layout="prev, pager, next"
          @current-change="loadPosts"
        />
      </div>
    </div>
    
    <el-dialog v-model="showPostDialog" title="发表新帖" width="600px">
      <el-form :model="newPost" label-width="80px">
        <el-form-item label="内容">
          <el-input
            v-model="newPost.content"
            type="textarea"
            :rows="6"
            placeholder="分享你对藏戏文化的见解..."
          />
        </el-form-item>
        <el-form-item label="图片">
          <el-upload
            action="#"
            list-type="picture-card"
            :auto-upload="false"
            :on-change="handleImageUpload"
          >
            <el-icon><Plus /></el-icon>
          </el-upload>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showPostDialog = false">取消</el-button>
        <el-button type="primary" @click="submitPost">发表</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { EditPen, ChatDotRound, Star, StarFilled, Plus } from '@element-plus/icons-vue'
import { getCommunityPosts, createPost, createComment, toggleLike } from '@/api'

const posts = ref([])
const currentPage = ref(1)
const pageSize = ref(10)
const total = ref(0)
const expandedPosts = ref([])
const commentInputs = reactive({})
const showPostDialog = ref(false)

const newPost = reactive({
  content: '',
  images: []
})

const mockPosts = [
  {
    id: 1,
    user: {
      username: '藏戏爱好者',
      avatar: 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png'
    },
    content: '今天观看了《文成公主》的演出,真是太震撼了!藏戏的唱腔和舞蹈都非常有特色,希望这项非物质文化遗产能够得到更好的传承。',
    images: [],
    likes: 15,
    comments: [
      {
        id: 1,
        user: {
          username: '文化传承人',
          avatar: 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png'
        },
        content: '说得好!藏戏是我们宝贵的文化遗产,需要我们共同守护。',
        createdAt: new Date(Date.now() - 3600000)
      }
    ],
    createdAt: new Date(Date.now() - 7200000)
  },
  {
    id: 2,
    user: {
      username: '雪域高原',
      avatar: 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png'
    },
    content: '刚刚体验了VR脸谱功能,太有趣了!能够亲身感受藏戏面具的魅力,这种创新的方式让传统文化更加贴近年轻人。',
    images: [],
    likes: 23,
    comments: [],
    createdAt: new Date(Date.now() - 10800000)
  },
  {
    id: 3,
    user: {
      username: '藏文化研究者',
      avatar: 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png'
    },
    content: '藏戏起源于14世纪,距今已有600多年的历史。它融合了歌舞、说唱、表演等多种艺术形式,是藏族人民智慧的结晶。希望更多人能够了解和喜爱藏戏文化。',
    images: [],
    likes: 31,
    comments: [
      {
        id: 2,
        user: {
          username: '学习中',
          avatar: 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png'
        },
        content: '感谢分享!长知识了',
        createdAt: new Date(Date.now() - 5400000)
      },
      {
        id: 3,
        user: {
          username: '藏戏迷',
          avatar: 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png'
        },
        content: '藏戏真的很有魅力,每次看都有新的感悟',
        createdAt: new Date(Date.now() - 4800000)
      }
    ],
    createdAt: new Date(Date.now() - 14400000)
  }
]

onMounted(() => {
  loadPosts()
})

const loadPosts = async () => {
  try {
    const res = await getCommunityPosts(currentPage.value, pageSize.value)
    posts.value = res.data.posts
    total.value = res.data.total
  } catch (error) {
    posts.value = mockPosts
    total.value = mockPosts.length
  }
}

const toggleComments = (postId) => {
  const index = expandedPosts.value.indexOf(postId)
  if (index > -1) {
    expandedPosts.value.splice(index, 1)
  } else {
    expandedPosts.value.push(postId)
  }
}

const submitComment = async (postId) => {
  const content = commentInputs[postId]
  if (!content || !content.trim()) {
    ElMessage.warning('请输入评论内容')
    return
  }
  
  try {
    await createComment({
      postId,
      content: content.trim()
    })
    
    commentInputs[postId] = ''
    ElMessage.success('评论成功')
    await loadPosts()
  } catch (error) {
    ElMessage.error('评论失败')
  }
}

const likePost = async (post) => {
  try {
    const res = await toggleLike(post.id)
    post.likes = res.data.likes
    post.likedByMe = res.data.likedByMe
    ElMessage.success(res.message)
  } catch (error) {
    ElMessage.error('请先登录')
  }
}

const submitPost = async () => {
  if (!newPost.content.trim()) {
    ElMessage.warning('请输入帖子内容')
    return
  }
  
  try {
    await createPost(newPost)
    
    newPost.content = ''
    newPost.images = []
    showPostDialog.value = false
    ElMessage.success('发表成功')
    await loadPosts()
  } catch (error) {
    ElMessage.error('发表失败')
  }
}

const handleImageUpload = (file) => {
  const reader = new FileReader()
  reader.onload = (e) => {
    newPost.images.push(e.target.result)
  }
  reader.readAsDataURL(file.raw)
}

const replyToComment = (postId, commentId) => {
  commentInputs[postId] = '@回复: '
}

const formatDate = (date) => {
  const now = new Date()
  const diff = now - new Date(date)
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  
  if (minutes < 60) {
    return `${minutes}分钟前`
  } else if (hours < 24) {
    return `${hours}小时前`
  } else if (days < 7) {
    return `${days}天前`
  } else {
    return new Date(date).toLocaleDateString('zh-CN')
  }
}
</script>

<style scoped lang="scss">
.community-container {
  background: white;
  border-radius: 10px;
  padding: 30px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.community-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  
  h2 {
    color: #8B4513;
    font-size: 28px;

    .title-tibetan {
      font-size: 17px;
      color: #A0522D;
      font-weight: 400;
      opacity: 0.7;
    }
  }

  .btn-tibetan {
    font-size: 13px;
    opacity: 0.8;
    margin-left: 4px;
  }
}

.posts-list {
  .post-card {
    background: #f9f9f9;
    border-radius: 10px;
    padding: 20px;
    margin-bottom: 20px;
    
    .post-header {
      margin-bottom: 15px;
      
      .user-info {
        display: flex;
        gap: 15px;
        align-items: center;
        
        .user-details {
          h4 {
            color: #333;
            margin-bottom: 5px;
          }
          
          .post-time {
            color: #999;
            font-size: 12px;
          }
        }
      }
    }
    
    .post-content {
      margin-bottom: 15px;
      
      p {
        color: #333;
        line-height: 1.8;
        margin-bottom: 15px;
      }
      
      .post-images {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
        gap: 10px;
        
        .post-image {
          width: 100%;
          height: 150px;
          border-radius: 8px;
          cursor: pointer;
        }
      }
    }
    
    .post-actions {
      display: flex;
      gap: 20px;
      padding-top: 15px;
      border-top: 1px solid #e0e0e0;

      .is-liked {
        color: #E6A23C !important;
        .el-icon { color: #E6A23C; }
      }
    }
    
    .comments-section {
      margin-top: 20px;
      padding-top: 20px;
      border-top: 1px solid #e0e0e0;
      
      .comment-input {
        display: flex;
        gap: 10px;
        margin-bottom: 20px;
        
        .el-textarea {
          flex: 1;
        }
      }
      
      .comments-list {
        .comment-item {
          display: flex;
          gap: 10px;
          margin-bottom: 15px;
          
          .comment-content {
            flex: 1;
            background: white;
            padding: 10px 15px;
            border-radius: 8px;
            
            .comment-header {
              display: flex;
              justify-content: space-between;
              margin-bottom: 5px;
              
              .username {
                font-weight: bold;
                color: #8B4513;
              }
              
              .comment-time {
                color: #999;
                font-size: 12px;
              }
            }
            
            p {
              color: #333;
              line-height: 1.6;
              margin-bottom: 5px;
            }
          }
        }
      }
    }
  }
  
  .pagination {
    display: flex;
    justify-content: center;
    margin-top: 30px;
  }
}
</style>
